import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { usePollVotes } from '@/hooks/usePollVotes';
import { ZapDialog } from '@/components/ZapDialog';
import { NoteContent } from '@/components/NoteContent';
import { Zap, Users, Clock, Timer, Trophy, CheckCircle2 } from 'lucide-react';
import type { ZapPoll } from '@/hooks/usePolls';

interface ZapPollCardProps {
  poll: ZapPoll;
  index: number;
}

export function ZapPollCard({ poll, index }: ZapPollCardProps) {
  const { user } = useCurrentUser();
  const { data: voteData, isLoading: isLoadingVotes } = usePollVotes(poll.event.id);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const isExpired = poll.closedAt ? (Date.now() / 1000) > poll.closedAt : false;

  const totalSats = voteData?.totalSats ?? 0;
  const totalVotes = voteData?.totalVotes ?? 0;

  const getPercentage = (optionIndex: string) => {
    if (!voteData || voteData.totalSats === 0) return 0;
    return Math.round(((voteData.satsByOption[optionIndex] || 0) / voteData.totalSats) * 100);
  };

  const getOptionSats = (optionIndex: string) => {
    if (!voteData) return 0;
    return voteData.satsByOption[optionIndex] || 0;
  };

  const getOptionVotes = (optionIndex: string) => {
    if (!voteData) return 0;
    return voteData.countByOption[optionIndex] || 0;
  };

  const leadingOptionIndex = useMemo(() => {
    if (!voteData || voteData.totalSats === 0) return null;
    let maxSats = 0;
    let leaderId: string | null = null;
    for (const [optionIdx, sats] of Object.entries(voteData.satsByOption)) {
      if (sats > maxSats) {
        maxSats = sats;
        leaderId = optionIdx;
      }
    }
    return leaderId;
  }, [voteData]);

  const selectedLabel = useMemo(() => {
    if (!selectedOption) return null;
    return poll.options.find(o => o.index === selectedOption)?.label ?? null;
  }, [selectedOption, poll.options]);

  const timeAgo = useMemo(() => {
    const seconds = Math.floor(Date.now() / 1000) - poll.event.created_at;
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  }, [poll.event.created_at]);

  const timeRemaining = useMemo(() => {
    if (!poll.closedAt) return null;
    const remaining = poll.closedAt - Math.floor(Date.now() / 1000);
    if (remaining <= 0) return 'Ended';
    if (remaining < 3600) return `${Math.floor(remaining / 60)}m left`;
    if (remaining < 86400) return `${Math.floor(remaining / 3600)}h left`;
    return `${Math.floor(remaining / 86400)}d left`;
  }, [poll.closedAt]);

  const cleanQuestion = useMemo(() => {
    let q = poll.question;
    q = q.replace(/nostr:[a-zA-Z0-9]+/g, '').trim();
    q = q.replace(/https?:\/\/[^\s]+/g, '').trim();
    q = q.replace(/\s+/g, ' ').trim();
    return q;
  }, [poll.question]);

  const formatSats = (sats: number) => {
    if (sats >= 1_000_000) return `${(sats / 1_000_000).toFixed(1)}M`;
    if (sats >= 1_000) return `${(sats / 1_000).toFixed(1)}k`;
    return sats.toLocaleString();
  };

  const handleOptionClick = (optionIndex: string) => {
    if (isExpired) return;
    setSelectedOption(prev => prev === optionIndex ? null : optionIndex);
  };

  return (
    <Card
      className="group relative overflow-hidden border-border/50 bg-card/80 backdrop-blur-sm transition-all duration-500 hover:border-primary/30 hover:shadow-[0_0_30px_rgba(247,147,26,0.08)] opacity-0 animate-fade-in-up"
      style={{ animationDelay: `${Math.min(index * 0.08, 0.6)}s` }}
    >
      <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-primary/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <CardContent className="p-5 sm:p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex-1 min-w-0">
            {cleanQuestion ? (
              <h3 className="text-base sm:text-lg font-semibold leading-snug text-foreground/95">
                {cleanQuestion}
              </h3>
            ) : (
              <div className="text-sm text-foreground/80 leading-relaxed">
                <NoteContent event={poll.event} />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isExpired ? (
              <Badge variant="secondary" className="text-[10px] uppercase tracking-wider bg-muted/80 text-muted-foreground border border-border/50">
                <Clock className="w-3 h-3 mr-1" />
                Ended
              </Badge>
            ) : timeRemaining ? (
              <Badge className="text-[10px] uppercase tracking-wider bg-primary/15 text-primary border border-primary/20">
                <Timer className="w-3 h-3 mr-1" />
                {timeRemaining}
              </Badge>
            ) : (
              <Badge className="text-[10px] uppercase tracking-wider bg-primary/15 text-primary border border-primary/20">
                <Zap className="w-3 h-3 mr-1" />
                Live
              </Badge>
            )}
          </div>
        </div>

        {/* Options — clickable to select */}
        <div className="space-y-2.5">
          {poll.options.map((option) => {
            const percentage = getPercentage(option.index);
            const optionSats = getOptionSats(option.index);
            const optionVotes = getOptionVotes(option.index);
            const isLeading = option.index === leadingOptionIndex;
            const isSelected = selectedOption === option.index;

            return (
              <button
                key={option.index}
                type="button"
                onClick={() => handleOptionClick(option.index)}
                disabled={isExpired}
                className={`
                  relative w-full text-left rounded-xl overflow-hidden transition-all duration-300
                  ${isExpired ? 'cursor-default' : 'cursor-pointer hover:scale-[1.01] active:scale-[0.99]'}
                  ${isSelected ? 'ring-2 ring-primary shadow-[0_0_12px_rgba(247,147,26,0.15)]' : ''}
                `}
              >
                {/* Background bar */}
                <div className="absolute inset-0 bg-secondary/40" />
                <div
                  className={`absolute inset-y-0 left-0 transition-all duration-700 ease-out ${
                    isSelected
                      ? 'bg-gradient-to-r from-primary/30 to-primary/15'
                      : isLeading && totalSats > 0
                        ? 'bg-gradient-to-r from-primary/25 to-primary/10'
                        : 'bg-gradient-to-r from-secondary/60 to-secondary/30'
                  }`}
                  style={{ width: `${percentage}%` }}
                />

                {/* Content */}
                <div className="relative flex items-center justify-between px-4 py-3.5">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {isSelected ? (
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                    ) : isLeading && totalSats > 0 ? (
                      <Trophy className="w-4 h-4 text-primary flex-shrink-0" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border-2 flex-shrink-0 border-muted-foreground/30" />
                    )}
                    <span className={`text-sm font-medium truncate ${
                      isSelected ? 'text-primary' : isLeading && totalSats > 0 ? 'text-foreground' : 'text-foreground/80'
                    }`}>
                      {option.label}
                    </span>
                  </div>

                  <div className="flex items-center gap-2.5 flex-shrink-0 ml-2">
                    {isLoadingVotes ? (
                      <Skeleton className="h-4 w-12" />
                    ) : totalSats > 0 ? (
                      <>
                        <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-0.5">
                          <Zap className="w-3 h-3" />
                          {formatSats(optionSats)}
                        </span>
                        <span className="text-xs text-muted-foreground/60 tabular-nums">
                          ({optionVotes})
                        </span>
                        <span className={`text-sm font-bold tabular-nums ${
                          isLeading ? 'text-primary' : 'text-muted-foreground'
                        }`}>
                          {percentage}%
                        </span>
                      </>
                    ) : null}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/30">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Zap className="w-3.5 h-3.5" />
              {isLoadingVotes ? (
                <Skeleton className="h-3 w-12" />
              ) : (
                <span className="tabular-nums">{formatSats(totalSats)} sats</span>
              )}
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              {isLoadingVotes ? (
                <Skeleton className="h-3 w-6" />
              ) : (
                <span className="tabular-nums">{totalVotes} vote{totalVotes !== 1 ? 's' : ''}</span>
              )}
            </span>
            <span>{timeAgo}</span>
          </div>

          {/* Vote CTA — only shows once an option is selected */}
          {!isExpired && user && selectedOption !== null && (
            <ZapDialog target={poll.event}>
              <Button
                size="sm"
                className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5 h-8 px-4 text-xs font-semibold"
              >
                <Zap className="w-3.5 h-3.5" />
                Zap for {selectedLabel}
              </Button>
            </ZapDialog>
          )}
          {!isExpired && user && selectedOption === null && (
            <span className="text-xs text-muted-foreground/60">
              Tap a name to vote
            </span>
          )}
          {!isExpired && !user && (
            <span className="text-xs text-primary/70 font-medium">
              Sign in to vote
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function ZapPollCardSkeleton() {
  return (
    <Card className="overflow-hidden border-border/50 bg-card/80">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-5 w-14 rounded-full" />
        </div>
        <div className="space-y-2.5">
          <Skeleton className="h-12 w-full rounded-xl" />
          <Skeleton className="h-12 w-full rounded-xl" />
        </div>
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/30">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-8 w-24 rounded-md" />
        </div>
      </CardContent>
    </Card>
  );
}
